import { createAsyncThunk } from '@reduxjs/toolkit'
import { taskTrackerApi } from '../../api/taskTracker.api'
import { mapProjectMemberToDraft } from '../../lib/projectMember.utils'
import {
  TASK_TRACKER_PERMISSION_OPTIONS,
  TaskTrackerPermissionKey,
} from '../../lib/taskTracker.permissions.constants'
import type { TaskTrackerProjectRole } from '../domain.types'
import { getErrorMessage } from '../shared/thunk.utils'

const KNOWN_TASK_TRACKER_PERMISSIONS = new Set(
  TASK_TRACKER_PERMISSION_OPTIONS.map((option) => option.value),
)

const mapRolePermissions = (role: TaskTrackerProjectRole) =>
  role.permissions.filter((permission): permission is TaskTrackerPermissionKey =>
    KNOWN_TASK_TRACKER_PERMISSIONS.has(permission as TaskTrackerPermissionKey),
  )

export const fetchProjectEditDataThunk = createAsyncThunk(
  'taskTracker/fetchProjectEditData',
  async (projectId: number, { rejectWithValue }) => {
    try {
      const project = await taskTrackerApi.getProject(projectId)
      const systemRoles = project.roles
        .filter((role) => role.isSystem)
        .sort((a, b) => a.title.localeCompare(b.title, 'ru'))

      return {
        projectId,
        projectTitle: project.title,
        board: project.board,
        members: project.members.map(mapProjectMemberToDraft),
        projectRoles: systemRoles,
        rolePermissionsDraft: Object.fromEntries(
          systemRoles.map((role) => [role.id, mapRolePermissions(role)]),
        ),
        rawMembers: project.members,
        roles: project.roles,
      }
    } catch (error: unknown) {
      return rejectWithValue({
        projectId,
        error: getErrorMessage(error, 'Не удалось загрузить данные проекта'),
      })
    }
  },
)

export const saveProjectTitleThunk = createAsyncThunk(
  'taskTracker/saveProjectTitle',
  async (
    payload: { projectId: number; projectTitle: string },
    { rejectWithValue },
  ) => {
    try {
      if (!payload.projectTitle.trim()) {
        return rejectWithValue('Название проекта не может быть пустым')
      }
      const project = await taskTrackerApi.updateProject(payload.projectId, {
        title: payload.projectTitle,
      })
      const systemRoles = project.roles
        .filter((role) => role.isSystem)
        .sort((a, b) => a.title.localeCompare(b.title, 'ru'))

      return {
        projectId: payload.projectId,
        projectTitle: project.title,
        board: project.board,
        members: project.members.map(mapProjectMemberToDraft),
        projectRoles: systemRoles,
        rolePermissionsDraft: Object.fromEntries(
          systemRoles.map((role) => [role.id, mapRolePermissions(role)]),
        ),
      }
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Не удалось сохранить проект'))
    }
  },
)

export const addProjectMemberThunk = createAsyncThunk(
  'taskTracker/addProjectMember',
  async (
    payload: { projectId: number; userId: number; roleId: number },
    { rejectWithValue },
  ) => {
    try {
      const member = await taskTrackerApi.addProjectMember(payload.projectId, {
        user_id: payload.userId,
        role_id: payload.roleId,
      })
      return {
        projectId: payload.projectId,
        member: mapProjectMemberToDraft(member),
        role: member.role,
      }
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Не удалось добавить участника'))
    }
  },
)

export const updateProjectMemberRoleThunk = createAsyncThunk(
  'taskTracker/updateProjectMemberRole',
  async (
    payload: { projectId: number; userId: number; roleId: number },
    { rejectWithValue },
  ) => {
    try {
      const member = await taskTrackerApi.addProjectMember(payload.projectId, {
        user_id: payload.userId,
        role_id: payload.roleId,
      })
      return {
        projectId: payload.projectId,
        member: mapProjectMemberToDraft(member),
        role: member.role,
      }
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Не удалось обновить роль'))
    }
  },
)

export const removeProjectMemberThunk = createAsyncThunk(
  'taskTracker/removeProjectMember',
  async (
    payload: { projectId: number; userId: number },
    { rejectWithValue },
  ) => {
    try {
      await taskTrackerApi.deleteProjectMember(payload.projectId, {
        user_id: payload.userId,
      })
      return payload
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Не удалось удалить участника'))
    }
  },
)

export const saveProjectRolePermissionsThunk = createAsyncThunk(
  'taskTracker/saveProjectRolePermissions',
  async (
    payload: {
      projectId: number
      roleId: number
      permissions: TaskTrackerPermissionKey[]
    },
    { rejectWithValue },
  ) => {
    try {
      const role = await taskTrackerApi.updateProjectRolePermissions(
        payload.projectId,
        payload.roleId,
        { permissions: payload.permissions },
      )
      return {
        projectId: payload.projectId,
        role,
        permissions: mapRolePermissions(role),
      }
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Не удалось сохранить права'))
    }
  },
)
